/**
 * center-more-options.js
 *
 * Centra horizontalmente el enlace "Más opciones de pago"
 * (.shopify-payment-button__more-options) exactamente debajo del botón
 * "Añadir al carrito" del producto principal Y dentro del quickview/modal,
 * IGNORANDO el bloque de Cantidad.
 *
 * Particularidades del theme que impone esta solución:
 *   - Hay múltiples instancias del enlace (producto principal,
 *     sticky-addtocart, quickview, modales). Procesamos todas EXCEPTO la
 *     del sticky-addtocart (esa tiene su propio layout).
 *   - El `shopify-payment-button` es un Custom Element que re-renderiza
 *     el enlace periódicamente y BORRA los atributos style que aplicamos.
 *     Por eso observamos cambios de atributos en el enlace y reaplicamos.
 *   - En estados intermedios el enlace puede medir solo 2–4px (mientras
 *     se carga el botón dinámico). Filtramos por ancho mínimo razonable
 *     para no centrarlo en posiciones equivocadas.
 *   - En la página de producto principal, el enlace va ENCIMA del ATC
 *     (top: -.5rem por CSS). Dentro del quickview/modal lo ponemos ABAJO
 *     del ATC para no tapar otros elementos.
 *
 * Para silenciar los logs pon DEBUG = false abajo.
 */
(function () {
  'use strict';

  var DEBUG = false;
  var SELECTOR_LINK = '.shopify-payment-button__more-options';
  var SELECTOR_ATC = '.m-add-to-cart';
  // Selectores adicionales para el ATC dentro del modal (no existe .m-add-to-cart
  // ahí; el botón visible es el de shopify-payment-button).
  var SELECTOR_ATC_FALLBACK = [
    'shopify-payment-button button[type="submit"]',
    'shopify-payment-button shopify-buy-it-now-button',
    'shopify-payment-button > div > button',
    'shopify-payment-button .shopify-payment-button__button',
    '.m-product-dynamic-checkout shopify-payment-button'
  ].join(', ');
  var SELECTOR_FORM_WRAP = '.m-product-form--wrapper';
  var SELECTOR_DYNAMIC = '.m-product-dynamic-checkout';
  // Selectores que indican que el enlace está dentro de un modal/quickview.
  var SELECTORS_MODAL = '.m-modal--content, .m-modal--wrapper, .m-quickview, .kg-quick-view-content, .m-product-quickview--info';
  // Sólo excluimos el sticky-addtocart (tiene su propio layout horizontal).
  var SELECTORS_EXCLUDE = '.m-sticky-addtocart';
  var MIN_LINK_WIDTH = 20; // px. Por debajo lo consideramos "no listo".

  function log() {
    if (!DEBUG) return;
    try {
      var args = ['[CMO]'].concat(Array.prototype.slice.call(arguments));
      console.log.apply(console, args);
    } catch (e) {}
  }

  function isVisible(el) {
    if (!el || !el.isConnected) return false;
    if (el.offsetWidth === 0 && el.offsetHeight === 0) return false;
    if (el.getClientRects().length === 0) return false;
    return true;
  }

  function isExcluded(link) {
    return !!link.closest(SELECTORS_EXCLUDE);
  }

  function isInModal(link) {
    return !!link.closest(SELECTORS_MODAL);
  }

  function findATC(form, link) {
    // 1) Busca el ATC del tema (página de producto principal).
    var atc = form.querySelector(SELECTOR_ATC);
    if (isVisible(atc)) return atc;
    // 2) Fallback: dentro del modal/quickview no existe .m-add-to-cart;
    //    usamos el botón principal del shopify-payment-button como referencia.
    var dyn = link.closest(SELECTOR_DYNAMIC) || form;
    var candidates = dyn.querySelectorAll(SELECTOR_ATC_FALLBACK);
    for (var i = 0; i < candidates.length; i++) {
      var c = candidates[i];
      if (!isVisible(c)) continue;
      // Saltamos el propio enlace o cualquiera dentro de él.
      if (c === link || c.contains(link) || link.contains(c)) continue;
      // Saltamos el "Más opciones" si lo coge por error.
      if (c.matches && c.matches('.shopify-payment-button__more-options')) continue;
      return c;
    }
    return null;
  }

  // Flag para evitar bucles del observer cuando el propio script cambia
  // el atributo style del enlace.
  var applyingFlag = new WeakSet();

  function applyBaseStyles(link) {
    applyingFlag.add(link);
    link.style.setProperty('position', 'absolute', 'important');
    link.style.setProperty('display', 'inline-block', 'important');
    link.style.setProperty('width', 'max-content', 'important');
    link.style.setProperty('max-width', '100%', 'important');
    link.style.setProperty('white-space', 'nowrap', 'important');
    link.style.setProperty('text-align', 'center', 'important');
    link.style.setProperty('right', 'auto', 'important');
    link.style.setProperty('margin-left', '0', 'important');
    link.style.setProperty('margin-right', '0', 'important');
    link.style.setProperty('transform', 'none', 'important');
    link.style.setProperty('font-size', '12px', 'important');
    link.style.setProperty('line-height', '1.5', 'important');
  }

  function applyPosition(link, leftValue, topValue) {
    applyingFlag.add(link);
    applyBaseStyles(link);
    link.style.setProperty('left', leftValue.toFixed(2) + 'px', 'important');
    if (typeof topValue === 'number') {
      link.style.setProperty('top', topValue.toFixed(2) + 'px', 'important');
      link.style.setProperty('bottom', 'auto', 'important');
    }
    setTimeout(function () { applyingFlag.delete(link); }, 0);
  }

  // Cache del último left/top calculado por enlace, para reaplicar rápido
  // cuando Shopify borra el style.
  var lastPosCache = new WeakMap();

  function centerLink(link) {
    if (!link || !link.isConnected) return;
    if (isExcluded(link)) return;        // ignora sticky-addtocart
    if (!isVisible(link)) return;

    var form = link.closest(SELECTOR_FORM_WRAP) || link.closest('form');
    if (!form) return;

    var atc = findATC(form, link);
    if (!isVisible(atc)) return;

    var inModal = isInModal(link);

    // offsetParent predecible: hacemos position:relative al .m-product-dynamic-checkout.
    var dyn = link.closest(SELECTOR_DYNAMIC);
    if (dyn && window.getComputedStyle(dyn).position === 'static') {
      dyn.style.setProperty('position', 'relative', 'important');
    }

    // Aplicamos primero los estilos base (sin left/top), forzar reflow.
    applyBaseStyles(link);
    void link.offsetWidth;
    setTimeout(function () { applyingFlag.delete(link); }, 0);

    var op = link.offsetParent;
    if (!op) return;

    // Tomamos el mejor ancho disponible (a veces offsetWidth es 0/3).
    var lw = Math.max(link.offsetWidth, link.scrollWidth, link.getBoundingClientRect().width);
    if (lw < MIN_LINK_WIDTH) {
      log('skip: linkWidth=', lw, '(menor que', MIN_LINK_WIDTH, ')');
      return;
    }

    var atcRect = atc.getBoundingClientRect();
    var opRect = op.getBoundingClientRect();
    var atcCenterX = atcRect.left + atcRect.width / 2;
    var atcCenterRel = atcCenterX - opRect.left;
    var leftValue = atcCenterRel - lw / 2;

    var topValue;
    if (inModal) {
      // Dentro del modal: colocamos el enlace JUSTO DEBAJO del botón ATC.
      // Calculamos el bottom del ATC relativo al offsetParent y le añadimos
      // un pequeño margen visual.
      topValue = (atcRect.bottom - opRect.top) + 6;
    }

    applyPosition(link, leftValue, topValue);
    lastPosCache.set(link, { left: leftValue, top: topValue });
    attachStyleObserver(link);

    log(inModal ? 'OK (modal)' : 'OK',
      'op=', (op.tagName || '') + '.' + ((op.className || '').toString().slice(0, 60)),
      'opLeft=', opRect.left.toFixed(1),
      'opWidth=', opRect.width.toFixed(1),
      'atcCenter=', atcCenterX.toFixed(1),
      'atcCenterRel=', atcCenterRel.toFixed(1),
      'linkWidth=', lw.toFixed(1),
      'leftAplicado=', leftValue.toFixed(1),
      'topAplicado=', (typeof topValue === 'number' ? topValue.toFixed(1) : '(CSS)')
    );
  }

  // Observa el atributo style del enlace; si Shopify lo borra/cambia,
  // reaplica la última posición calculada.
  function attachStyleObserver(link) {
    if (link._cmoStyleObserver) return;
    var obs = new MutationObserver(function (records) {
      if (applyingFlag.has(link)) return;
      var styleChanged = records.some(function (r) {
        return r.type === 'attributes' && r.attributeName === 'style';
      });
      if (!styleChanged) return;
      var cached = lastPosCache.get(link);
      if (!cached || typeof cached.left !== 'number') return;
      // Si el style.left fue borrado o cambiado por otro código, reaplica.
      var currentLeft = parseFloat(link.style.left);
      if (isNaN(currentLeft) || Math.abs(currentLeft - cached.left) > 0.5) {
        log('reapply (style cambiado por terceros)');
        applyPosition(link, cached.left, cached.top);
      }
    });
    obs.observe(link, { attributes: true, attributeFilter: ['style'] });
    link._cmoStyleObserver = obs;
  }

  function centerAll() {
    var links = document.querySelectorAll(SELECTOR_LINK);
    for (var i = 0; i < links.length; i++) centerLink(links[i]);
  }

  var pending = false;
  function scheduleCenter() {
    if (pending) return;
    pending = true;
    requestAnimationFrame(function () {
      pending = false;
      centerAll();
    });
  }

  // Observa el body para detectar la inserción dinámica del modal/quickview
  // y de cualquier nuevo .m-product-form--wrapper. Cada vez que aparece uno
  // nuevo, lanza el centrado y le engancha su propio MutationObserver
  // específico (childList) para reaccionar a los re-renders internos del
  // shopify-payment-button.
  function watchFormWrapper(wrap) {
    if (wrap._cmoFormObserver) return;
    var fo = new MutationObserver(function (records) {
      var relevant = records.some(function (r) {
        return r.type === 'childList' && (r.addedNodes.length || r.removedNodes.length);
      });
      if (!relevant) return;
      clearTimeout(fo._t);
      fo._t = setTimeout(scheduleCenter, 80);
    });
    fo.observe(wrap, { childList: true, subtree: true, attributes: false });
    wrap._cmoFormObserver = fo;
  }

  function init() {
    log('init');
    centerAll();
    [100, 300, 800, 2000, 5000].forEach(function (d) {
      setTimeout(centerAll, d);
    });

    var resizeTimer;
    window.addEventListener('resize', function () {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(scheduleCenter, 80);
    });

    document.querySelectorAll(SELECTOR_FORM_WRAP).forEach(watchFormWrapper);

    // Observer global del body: detecta nuevos m-product-form--wrapper
    // (sucede al abrirse el modal del quickview).
    var bodyObs = new MutationObserver(function (records) {
      var newWrap = false;
      for (var i = 0; i < records.length; i++) {
        var added = records[i].addedNodes;
        for (var j = 0; j < added.length; j++) {
          var n = added[j];
          if (n.nodeType !== 1) continue;
          if (n.matches && n.matches(SELECTOR_FORM_WRAP)) {
            watchFormWrapper(n);
            newWrap = true;
          } else if (n.querySelectorAll) {
            n.querySelectorAll(SELECTOR_FORM_WRAP).forEach(function (w) {
              watchFormWrapper(w);
              newWrap = true;
            });
          }
        }
      }
      if (newWrap) {
        clearTimeout(bodyObs._t);
        bodyObs._t = setTimeout(scheduleCenter, 80);
      }
    });
    bodyObs.observe(document.body, { childList: true, subtree: true, attributes: false });

    if ('ResizeObserver' in window) {
      var ro = new ResizeObserver(function () { scheduleCenter(); });
      document.querySelectorAll(SELECTOR_ATC).forEach(function (b) { ro.observe(b); });
      document.querySelectorAll(SELECTOR_FORM_WRAP).forEach(function (w) { ro.observe(w); });
    }

    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(centerAll).catch(function () {});
    }

    window.addEventListener('load', centerAll);

    // Eventos del quickview de Minimog: cuando termina de cargarse,
    // forzamos varios pases de centrado (el shopify-payment-button tarda
    // un poco en renderizar el botón).
    document.addEventListener('quick-view:loaded', function () {
      [50, 200, 500, 1000, 2000].forEach(function (d) {
        setTimeout(scheduleCenter, d);
      });
    });
    document.addEventListener('tcustomizer-custom-show-quick-view', function () {
      [50, 200, 500, 1000, 2000].forEach(function (d) {
        setTimeout(scheduleCenter, d);
      });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
